import React, { useEffect, useMemo, useState } from "react";
import "../styles/AdminUsersPage.css";
import { canManageOrganization } from "../utils/roles";
import PaginationControls from "../components/PaginationControls";
import { apiClient } from "../services/apiClient";
import { useAuth } from "../contexts/AuthContext";
import { useI18n } from "../providers/I18nProvider";
import NoirAlert from "../components/NoirAlert";
import { TabBar, FilterBar, SearchInput, EmptyState } from "../components/ListToolbar";

const TOOL_DEFS = [
  { key: "tools", label: "Tools", description: "ComInterface Generator" },
  { key: "cms", label: "BTO Website", description: "Web Sitesi İçerik Yönetimi" },
  { key: "gitlab_editor", label: "GitLab Editor", description: "Proje & Şablon Yönetimi" },
  { key: "applications", label: "Başvurular", description: "İş Başvuru İncelemesi" },
  { key: "cctv", label: "Ofis Kameraları", description: "CCTV Canlı İzleme" },
  { key: "leave_report", label: "İzin Dökümü", description: "Anasayfada hızlı izin/iş gücü Excel raporu" },
];

// IT rolüne otomatik verilmeyen araçlar (backend ile aynı): bunlar IT'ye de
// yalnızca açık izinle verilir.
const AUTO_EXCLUDED_FOR_IT = new Set(["cctv", "leave_report"]);
// Belirli bir araç bu kullanıcıya rolü gereği otomatik mi geliyor?
const isAutoForTool = (user, toolKey) => {
  const role = (user.role_name || "").toLowerCase();
  if (role === "admin") return true;
  if (role === "it") return !AUTO_EXCLUDED_FOR_IT.has(toolKey);
  return false;
};

const AdminToolPermissionsPage = () => {
  const { t } = useI18n();
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [allPermissions, setAllPermissions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [savingUserId, setSavingUserId] = useState(null);
  const [notification, setNotification] = useState({ message: "", type: "" });
  const [tab, setTab] = useState("all"); // all | granted | none
  const [roleFilter, setRoleFilter] = useState("");
  const [toolFilter, setToolFilter] = useState("");

  const canManage = canManageOrganization(me);

  const pageSizeKey = "pageSize.adminToolPermissions";
  const [pageSize, setPageSize] = useState(() => {
    const raw = localStorage.getItem(pageSizeKey);
    const parsed = raw ? parseInt(raw, 10) : 10;
    return [10, 20, 30].includes(parsed) ? parsed : 10;
  });
  const [page, setPage] = useState(1);

  useEffect(() => {
    localStorage.setItem(pageSizeKey, String(pageSize));
    setPage(1);
  }, [pageSize]);

  const fetchAll = async () => {
    setLoading(true);
    setError("");
    try {
      const [usersRes, permsRes] = await Promise.all([
        apiClient.get("/users"),
        apiClient.get("/tool-permissions"),
      ]);
      setUsers(usersRes.data || []);
      setAllPermissions(permsRes.data || []);
    } catch (err) {
      setError(err.response?.data?.msg || err.message || "Veriler yüklenemedi.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAll();
  }, []);

  // userId -> Set(tool_key) map
  const permissionMap = useMemo(() => {
    const map = new Map();
    for (const row of allPermissions) {
      if (!map.has(row.user_id)) map.set(row.user_id, new Set());
      map.get(row.user_id).add(row.tool_key);
    }
    return map;
  }, [allPermissions]);

  const hasAnyAccess = (u) =>
    (permissionMap.get(u.id)?.size || 0) > 0 || TOOL_DEFS.some((tool) => isAutoForTool(u, tool.key));
  const hasTool = (u, key) => isAutoForTool(u, key) || permissionMap.get(u.id)?.has(key);

  const tabCounts = useMemo(
    () => ({
      all: users.length,
      granted: users.filter((u) => (permissionMap.get(u.id)?.size || 0) > 0).length,
      none: users.filter((u) => !hasAnyAccess(u)).length,
    }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [users, permissionMap]
  );

  const roleOptions = useMemo(
    () =>
      Array.from(new Set(users.map((u) => u.role_name).filter(Boolean))).sort((a, b) =>
        a.localeCompare(b, "tr")
      ),
    [users]
  );

  const filteredUsers = useMemo(() => {
    const q = search.trim().toLocaleLowerCase("tr-TR");
    return users.filter((u) => {
      if (tab === "granted" && !((permissionMap.get(u.id)?.size || 0) > 0)) return false;
      if (tab === "none" && hasAnyAccess(u)) return false;
      if (roleFilter && u.role_name !== roleFilter) return false;
      if (toolFilter && !hasTool(u, toolFilter)) return false;
      if (!q) return true;
      return [u.name, u.surname, u.username, u.email]
        .filter(Boolean)
        .join(" ")
        .toLocaleLowerCase("tr-TR")
        .includes(q);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [users, search, tab, roleFilter, toolFilter, permissionMap]);

  useEffect(() => {
    setPage(1);
  }, [search, tab, roleFilter, toolFilter]);

  const hasActiveFilter = Boolean(search || roleFilter || toolFilter);
  const resetFilters = () => {
    setSearch("");
    setRoleFilter("");
    setToolFilter("");
  };

  useEffect(() => {
    const totalPages = Math.ceil((filteredUsers.length || 0) / pageSize) || 1;
    if (page > totalPages) setPage(totalPages);
  }, [filteredUsers.length, page, pageSize]);

  const pagedUsers = useMemo(() => {
    const start = (page - 1) * pageSize;
    return filteredUsers.slice(start, start + pageSize);
  }, [filteredUsers, page, pageSize]);

  const toggleTool = async (user, toolKey) => {
    if (!canManage) return;
    if (isAutoForTool(user, toolKey)) return; // rolü gereği otomatik erişiyor

    const current = permissionMap.get(user.id) || new Set();
    const hadIt = current.has(toolKey);
    const nextSet = new Set(current);
    if (hadIt) nextSet.delete(toolKey);
    else nextSet.add(toolKey);

    if (savingUserId != null) return; // devam eden kayıt varken yarış olmasın
    setSavingUserId(user.id);
    setError("");
    try {
      await apiClient.put(`/tool-permissions/user/${user.id}`, {
        tools: Array.from(nextSet),
      });
      // Yerel state'i güncelle
      setAllPermissions((prev) => {
        const filtered = prev.filter((r) => r.user_id !== user.id);
        const added = Array.from(nextSet).map((tk) => ({
          user_id: user.id,
          tool_key: tk,
        }));
        return [...filtered, ...added];
      });
      const toolLabel = TOOL_DEFS.find((d) => d.key === toolKey)?.label || toolKey;
      setNotification({
        message: `${user.name} ${user.surname}: ${toolLabel} ${
          hadIt ? t("adminTools.revoked") : t("adminTools.granted")
        }`,
        type: "success",
      });
    } catch (err) {
      setNotification({
        message: err.response?.data?.msg || err.message || t("adminTools.updateFailed"),
        type: "error",
      });
    } finally {
      setSavingUserId(null);
    }
  };

  if (loading) return <div className="admin-users-page">Yükleniyor…</div>;

  return (
    <div className="admin-users-page">
      <div className="page-header">
        <h2>Araç Erişim İzinleri</h2>
        <p className="section-subtitle">
          Kullanıcılara araç erişimi verin. Admin tüm araçlara otomatik erişir; IT rolü
          Ofis Kameraları ve İzin Dökümü hariç diğer araçlara otomatik erişir (bu ikisi
          için açık izin gerekir).
        </p>
      </div>

      <NoirAlert
        message={notification.message}
        type={notification.type}
        onClose={() => setNotification({ message: "", type: "" })}
      />
      {error && <div className="error-banner">{error}</div>}

      <TabBar
        value={tab}
        onChange={setTab}
        tabs={[
          { key: "all", label: t("adminTools.tabs.all"), count: tabCounts.all },
          { key: "granted", label: t("adminTools.tabs.granted"), count: tabCounts.granted },
          { key: "none", label: t("adminTools.tabs.none"), count: tabCounts.none },
        ]}
      />
      <FilterBar showReset={hasActiveFilter} onReset={resetFilters}>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder={t("adminTools.search")}
        />
        <select
          className="leaves-filter-input lt-input"
          value={roleFilter}
          onChange={(e) => setRoleFilter(e.target.value)}
          aria-label={t("adminTools.role")}
        >
          <option value="">{t("adminTools.allRoles")}</option>
          {roleOptions.map((r) => (
            <option key={r} value={r}>
              {r}
            </option>
          ))}
        </select>
        <select
          className="leaves-filter-input lt-input"
          value={toolFilter}
          onChange={(e) => setToolFilter(e.target.value)}
          aria-label={t("adminTools.tool")}
        >
          <option value="">{t("adminTools.allTools")}</option>
          {TOOL_DEFS.map((tool) => (
            <option key={tool.key} value={tool.key}>
              {tool.label}
            </option>
          ))}
        </select>
      </FilterBar>

      <div className="table-wrapper">
        <table className="admin-table perm-table">
          <thead>
            <tr>
              <th>Kullanıcı</th>
              <th>Rol</th>
              {TOOL_DEFS.map((tool) => (
                <th key={tool.key} style={{ textAlign: "center" }}>
                  <div>{tool.label}</div>
                  <div style={{ fontSize: "0.75em", opacity: 0.65 }}>{tool.description}</div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {pagedUsers.map((user) => {
              const granted = permissionMap.get(user.id) || new Set();
              const saving = savingUserId === user.id;
              return (
                <tr key={user.id}>
                  <td data-label={t("adminTools.user")}>
                    <div style={{ fontWeight: 600 }}>
                      {user.name} {user.surname}
                    </div>
                    <div style={{ fontSize: "0.8em", opacity: 0.7 }}>
                      {user.username} · {user.email}
                    </div>
                  </td>
                  <td data-label={t("adminTools.role")}>{user.role_name}</td>
                  {TOOL_DEFS.map((tool) => {
                    const auto = isAutoForTool(user, tool.key);
                    const checked = auto || granted.has(tool.key);
                    return (
                      <td key={tool.key} data-label={tool.label} className="perm-cell" style={{ textAlign: "center" }}>
                        <input
                          type="checkbox"
                          aria-label={`${user.name} ${user.surname} – ${tool.label}`}
                          checked={checked}
                          disabled={!canManage || auto || saving}
                          onChange={() => toggleTool(user, tool.key)}
                          title={auto ? t("adminTools.autoByRole") : ""}
                        />
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      {filteredUsers.length === 0 && <EmptyState message={t("adminTools.emptyFiltered")} />}

      <PaginationControls
        page={page}
        pageSize={pageSize}
        totalItems={filteredUsers.length}
        onPageChange={setPage}
        onPageSizeChange={setPageSize}
      />
    </div>
  );
};

export default AdminToolPermissionsPage;
